import React, { useEffect, useState } from "react";
import { KeyRound, RefreshCw, Copy, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/adminAuth";
import { api } from "../../lib/api";

/**
 * /admin/pins — administración de PINs de canje para los bares.
 *
 * Lista todos los venues + su PIN actual (si existe) + botón para regenerar.
 * Al generar, muestra el PIN en claro (única vez) + texto WhatsApp pre-armado.
 */
export default function AdminPins() {
  const [venues, setVenues] = useState([]);
  const [pins, setPins] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [vs, ps] = await Promise.all([
        api.listVenues(),
        adminApi.get("/venue/admin/pin/list"),
      ]);
      setVenues(vs);
      const map = {};
      (ps || []).forEach((p) => { map[p.venue_id] = p; });
      setPins(map);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const generatePin = async (venue) => {
    if (pins[venue.id] && !window.confirm(`Ya existe un PIN activo para ${venue.name}. ¿Regenerarlo invalidará el anterior. Continuar?`)) {
      return;
    }
    setGenerating(venue.id);
    try {
      const res = await adminApi.post("/venue/admin/pin/generate", { venue_id: venue.id });
      setModal(res);
      load();
    } catch (e) {
      alert("Error generando PIN: " + (e.message || "desconocido"));
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-pins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl flex items-center gap-2"><KeyRound size={24} /> PINs de reclamo</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Cada bar recibe un PIN único que usa en <strong className="text-[#FEBA01]">/canjear</strong> para validar los Huesos que reclaman los clientes.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 border-2 border-[#333] hover:border-[#FEBA01] px-3 py-2 text-sm"
          data-testid="pins-refresh-btn"
        >
          <RefreshCw size={14} /> Refrescar
        </button>
      </div>

      <div className="bg-[#0A0A0A] border-2 border-[#333] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#111]">
            <tr>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#FEBA01]">Local</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#FEBA01]">Barrio</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#FEBA01]">PIN</th>
              <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#FEBA01]">Creado</th>
              <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-[#FEBA01]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-[#A3A3A3]">Cargando...</td></tr>
            )}
            {!loading && venues.map((v) => {
              const pin = pins[v.id];
              return (
                <tr key={v.id} className="border-t border-[#222]" data-testid={`pin-row-${v.id}`}>
                  <td className="px-4 py-3 font-medium">{v.name}</td>
                  <td className="px-4 py-3 text-[#A3A3A3]">{v.neighborhood || "-"}</td>
                  <td className="px-4 py-3">
                    {pin ? (
                      <span className="inline-flex items-center gap-2 px-2 py-1 bg-[#FEBA01]/10 border border-[#FEBA01] text-[#FEBA01] font-mono text-xs">
                        <CheckCircle2 size={12} /> {pin.prefix}-**** (activo)
                      </span>
                    ) : (
                      <span className="text-[#666] text-xs">sin PIN</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                    {pin ? new Date(pin.created_at).toLocaleDateString("es-CL") : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => generatePin(v)}
                      disabled={generating === v.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FEBA01] text-black font-semibold text-xs uppercase tracking-wide hover:bg-white disabled:opacity-50"
                      data-testid={`pin-generate-${v.id}`}
                    >
                      {generating === v.id ? <Loader2 className="animate-spin" size={12} /> : <KeyRound size={12} />}
                      {pin ? "Regenerar" : "Generar PIN"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && <PinModal data={modal} onClose={() => setModal(null)} />}
    </div>
  );
}

function PinModal({ data, onClose }) {
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const openWhatsApp = () => {
    const encoded = encodeURIComponent(data.whatsapp_message);
    window.open(`https://wa.me/?text=${encoded}`, "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0A0A0A] border-2 border-[#FEBA01] max-w-lg w-full p-6 shadow-brutal-white"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-2xl mb-1">PIN generado</h2>
        <p className="text-sm text-[#A3A3A3] mb-4">
          <strong>Este PIN solo se muestra una vez.</strong> Cópialo o envíalo al bar ahora.
        </p>

        <div className="bg-[#FEBA01] text-black p-4 font-mono text-2xl text-center tracking-widest mb-2" data-testid="pin-display">
          {data.pin}
        </div>

        <button
          type="button"
          onClick={() => copy(data.pin, "pin")}
          className="w-full inline-flex items-center justify-center gap-2 border-2 border-[#333] hover:border-[#FEBA01] px-3 py-2 text-sm mb-4"
          data-testid="pin-copy"
        >
          <Copy size={14} /> {copied === "pin" ? "¡Copiado!" : "Copiar PIN"}
        </button>

        <div className="mt-4 border-t border-[#333] pt-4">
          <div className="text-xs uppercase tracking-widest text-[#FEBA01] mb-2">Mensaje WhatsApp listo</div>
          <pre className="bg-black border-2 border-[#222] p-3 text-xs text-[#E5E5E5] whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{data.whatsapp_message}</pre>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => copy(data.whatsapp_message, "wa")}
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#333] hover:border-[#FEBA01] px-3 py-2 text-sm"
              data-testid="wa-copy"
            >
              <Copy size={14} /> {copied === "wa" ? "¡Copiado!" : "Copiar mensaje"}
            </button>
            <button
              type="button"
              onClick={openWhatsApp}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-black px-3 py-2 text-sm font-semibold"
              data-testid="wa-open"
            >
              <MessageCircle size={14} /> Abrir WhatsApp
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-4 px-3 py-2 text-sm text-[#A3A3A3] hover:text-white"
          data-testid="pin-close"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
